import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowRight, CheckCircle2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { apiFetch, ApiError } from '../../lib/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { AuthPageLayout } from '../../components/auth/AuthPageLayout';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Reset token is missing or invalid.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      await apiFetch('/api/v1/auth/password-reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: password }),
      });
      setSuccess(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && typeof err.details?.detail === 'string') {
        setError(err.details.detail);
      } else if (err instanceof Error) {
        setError(err.message || 'Unable to reset password.');
      } else {
        setError('Unable to reset password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const INPUT =
    'block w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-[#40b93c] focus:outline-none focus:ring-2 focus:ring-[#40b93c]/20';

  return (
    <AuthPageLayout showBackToLogin>
      <div className="mb-6 space-y-2">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Credential update
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Set a new password</h1>
        <p className="mt-1 text-sm text-slate-500">Choose a strong password for your account</p>
      </div>

      {!token ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            This reset link is invalid. Request a new one.
          </div>
          <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1f4778] hover:text-[#14315a] hover:underline">
            Request new reset link <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : success ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2.5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            Password updated successfully.
          </div>
          <button
            type="button"
            onClick={() => navigate('/login?reset=success', { replace: true })}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#40b93c] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2f962d] hover:shadow-[0_8px_20px_rgba(64,185,60,0.35)]"
          >
            Continue to sign in
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label htmlFor="new-password" className="mb-1.5 block text-sm font-medium text-slate-700">
              New password
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${INPUT} pr-10`}
                placeholder="Enter a new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium text-slate-700">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={INPUT}
              placeholder="Confirm your new password"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#40b93c] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2f962d] hover:shadow-[0_8px_20px_rgba(64,185,60,0.35)] focus:outline-none focus:ring-2 focus:ring-[#40b93c] focus:ring-offset-2 disabled:translate-y-0 disabled:opacity-50"
          >
            {isLoading ? <LoadingSpinner size="sm" /> : 'Update password'}
          </button>
        </form>
      )}
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
